import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import KinAdminContributions from '../components/KinAdminContributions';
import KinAdminMemberPositions from '../components/KinAdminMemberPositions';
import SupplierSettings from '../components/SupplierSettings';
import SavingsOpportunities from '../components/SavingsOpportunities';

/**
 * KinAdminDashboard
 *
 * KC-only management console for a single Kin.
 *
 * Route: /kin-admin/:kinId
 *
 * Accordion layout with action panels at the top and informational
 * panels below. Additional panels (Invitations, Savings, Draft
 * consolidation, Vendor payments) migrate here from the Kin Dashboard
 * in later sessions.
 */
export default function KinAdminDashboard() {
  const { kinId: routeKinId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const kinId = routeKinId || user?.activeKinId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kin, setKin] = useState(null);
  const [funding, setFunding] = useState(null);

  const [openPanels, setOpenPanels] = useState({
    contributions: true,
    memberPositions: false,
    suppliers: false,
    invitations: false,
    savings: false,
    settings: false,
  });

  useEffect(() => {
    if (kinId) loadAll();
  }, [kinId]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [kinRes, fundingRes] = await Promise.all([
        fetch(`/api/groups/${kinId}`, { headers }),
        fetch(`/api/kins/${kinId}/funding`, { headers }),
      ]);

      if (!kinRes.ok) throw new Error('Failed to load Kin');
      const kinJson = await kinRes.json();
      setKin(kinJson.group || kinJson);

      if (fundingRes.ok) {
        const f = await fundingRes.json();
        if (f.role !== 'kc') {
          // Not the KC — redirect away
          navigate('/kin-dashboard');
          return;
        }
        setFunding(f);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePanel = (name) => {
    setOpenPanels((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const fmt = (n) => '$' + (Number(n) || 0).toFixed(2);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-12 text-gray-500">Loading admin…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-12 text-red-600">Error: {error}</div>
        <div className="text-center mt-4">
          <Link to="/kin-dashboard" className="text-blue-600 hover:underline">
            ← Back to Kin Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const pendingCount = funding?.members?.reduce(
    (sum, m) => sum + (m.pendingCount || 0),
    0
  ) || 0;
  const pendingTotal = funding?.members?.reduce(
    (sum, m) => sum + (m.pendingPledge || 0),
    0
  ) || 0;

  // Which pending rows have an off-system receipt (still to deposit to VA)
  // We don't know from the funding summary alone; the contributions panel
  // fetches its own data. For the header, just count pendings.

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            ⚙️ Kin Admin
            {kin?.name ? ` — ${kin.name}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Management console for this Kin
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/kin-dashboard"
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            ← Back to Kin Dashboard
          </Link>
        </div>
      </div>

      {/* Quick stats / needs attention */}
      {funding && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Total balance
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {fmt(funding.totalAvailable)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Available for orders
            </div>
            <div className="text-2xl font-bold text-green-700">
              {fmt(funding.availableForOrders)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Security deposit
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {fmt(funding.securityDeposit?.total || 0)}
            </div>
          </div>
          <div className={`rounded-lg shadow p-4 ${pendingCount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-white'}`}>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Pending contributions
            </div>
            <div className="text-2xl font-bold text-amber-700">
              {pendingCount > 0 ? fmt(pendingTotal) : '—'}
            </div>
            {pendingCount > 0 && (
              <div className="text-xs text-amber-600 mt-0.5">
                {pendingCount} outstanding
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action panels */}
      <div className="space-y-3">
        {/* Pending contributions */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('contributions')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.contributions ? '▼' : '▶'}</span>
              <span className="font-semibold">
                🔔 Pending contributions{pendingCount > 0 ? ` (${pendingCount})` : ''}
              </span>
            </div>
            {pendingCount > 0 && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                needs action
              </span>
            )}
          </button>
          {openPanels.contributions && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <KinAdminContributions kinId={kinId} />
            </div>
          )}
        </div>

        {/* Member positions */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('memberPositions')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.memberPositions ? '▼' : '▶'}</span>
              <span className="font-semibold">
                👥 Member positions
                {funding?.memberCount != null ? ` (${funding.memberCount})` : ''}
              </span>
            </div>
          </button>
          {openPanels.memberPositions && (
            <div className="border-t border-gray-100">
              <KinAdminMemberPositions members={funding?.members || []} />
            </div>
          )}
        </div>

        {/* Suppliers */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('suppliers')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.suppliers ? '▼' : '▶'}</span>
              <span className="font-semibold">🏪 Supplier management</span>
            </div>
          </button>
          {openPanels.suppliers && (
            <div className="p-4 border-t border-gray-100">
              <SupplierSettings kinId={kinId} />
            </div>
          )}
        </div>
        {/* Invitations */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('invitations')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.invitations ? '▼' : '▶'}</span>
              <span className="font-semibold">📨 Invitations</span>
            </div>
          </button>
          {openPanels.invitations && (
            <div className="p-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Invitations will appear here once you send them.
              </p>
            </div>
          )}
        </div>

        {/* Savings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('savings')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.savings ? '▼' : '▶'}</span>
              <span className="font-semibold">💎 Savings opportunities</span>
            </div>
          </button>
          {openPanels.savings && (
            <div className="p-4 border-t border-gray-100">
              <SavingsOpportunities kinId={kinId} />
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <button
            onClick={() => togglePanel('settings')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{openPanels.settings ? '▼' : '▶'}</span>
              <span className="font-semibold">⚙️ Kin settings</span>
            </div>
          </button>
          {openPanels.settings && (
            <div className="p-4 border-t border-gray-100 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Invite Code</h4>
                <div className="flex items-center gap-2">
                  <code className="bg-white px-3 py-1 rounded border">
                    {kin?.inviteCode || '(loading)'}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(kin?.inviteCode || '');
                      alert('Invite code copied!');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    📋 Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Share this code with people you want to invite to your Kin.
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Coordinator Compensation</h4>
                  <p className="text-sm text-gray-600">
                    Type: {kin?.coordinatorCompensation?.type || 'Volunteer'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Percentage: {kin?.coordinatorCompensation?.percentage || 0}%
                  </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Community Fund</h4>
                <p className="text-sm text-gray-600">
                  Goal: ${kin?.communityFund?.goal || 0}
                </p>
                <p className="text-sm text-gray-600">
                  Deadline: {kin?.communityFund?.deadline ? new Date(kin.communityFund.deadline).toLocaleDateString('en-AU') : 'not set'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
